import { useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Globe,
  Mail,
  Phone,
  Plus,
  Pencil,
  Trash2,
  FolderKanban,
  CheckSquare,
  Users,
  Upload,
  FileText,
  AlertCircle,
  KeyRound,
  Eye,
  EyeOff,
  Copy,
  ExternalLink,
  Loader2,
} from 'lucide-react'
import { clientsApi, projectsApi, filesApi, quotesApi } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { getApiErrorMessage } from '@/services/api'
import { Badge } from '@/components/common/Badge'
import { Card, CardHeader } from '@/components/common/Card'
import { StatCard } from '@/components/common/StatCard'
import { cn } from '@/lib/utils'
import { confirm } from '@/lib/confirm'
import { toast } from '@/lib/toast'
import { ClientForm } from '@/components/clients/ClientForm'
import type { Client, Project, Contact, ClientStatus, Quote, QuoteStatus } from '@/types'

// ============================================================
// TYPES
// ============================================================

// The backend wraps responses in { success: boolean; data: T }
interface ApiEnvelope<T> {
  success: boolean
  data: T
}

// Backend getClientById includes contacts, modules, projects and _count
interface ClientWithRelations extends Client {
  projects?: (Project & { _count?: { tasks: number } })[]
  _count?: {
    projects: number
    tasks: number
    contacts: number
  }
}

type TabId = 'overview' | 'projects' | 'quotes' | 'contacts' | 'documents' | 'credentials'

interface NewContactForm {
  firstName: string
  lastName: string
  email: string
  phone: string
  role: string
  isPrimary: boolean
}

// ============================================================
// CONSTANTS
// ============================================================

const BASE_TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'projects', label: 'Progetti' },
  { id: 'quotes', label: 'Preventivi' },
  { id: 'contacts', label: 'Contatti' },
  { id: 'documents', label: 'Documenti' },
]

const STATUS_VARIANT: Record<ClientStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  PROSPECT: 'info', ACTIVE: 'success', INACTIVE: 'warning', CHURNED: 'danger',
}
const STATUS_LABEL: Record<ClientStatus, string> = {
  PROSPECT: 'Prospect', ACTIVE: 'Attivo', INACTIVE: 'Inattivo', CHURNED: 'Perso',
}
const PROJECT_STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  PLANNING: 'bg-primary-50 text-primary-700',
  ACTIVE: 'bg-success-50 text-success-700',
  ON_HOLD: 'bg-warning-50 text-warning-700',
  COMPLETED: 'bg-success-50 text-success-700',
  CANCELLED: 'bg-danger-50 text-danger-700',
}
const PROJECT_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Bozza', PLANNING: 'Pianificazione', ACTIVE: 'Attivo', ON_HOLD: 'In Pausa',
  COMPLETED: 'Completato', CANCELLED: 'Annullato',
}
const MODULE_LABELS: Record<string, string> = {
  SOCIAL: 'Social Media Management',
  ADV: 'Advertising & Campagne Paid',
  EVENTI: 'Organizzazione Eventi',
  DIGITAL: 'Digital Hub (Web, SEO, App)',
  PR: 'Pubbliche Relazioni & Ufficio Stampa',
  BRANDING: 'Branding & Identità Visiva',
  CONSULENZA: 'Consulenza Strategica',
}

const EMPTY_CONTACT: NewContactForm = {
  firstName: '', lastName: '', email: '', phone: '', role: '', isPrimary: false,
}

// Input style shared across inline forms
const inputClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg',
  'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
  'placeholder:text-slate-400 transition-colors'
)
const labelClass = 'block text-xs font-medium text-slate-600 mb-1'

// ============================================================
// SKELETON LOADER
// ============================================================

function TabSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-16 bg-slate-100 rounded-2xl" />
      ))}
    </div>
  )
}

// ============================================================
// ERROR BANNER
// ============================================================

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
      {message}
    </div>
  )
}

// ============================================================
// OVERVIEW TAB
// ============================================================

function InfoField({ label, value }: { label: string; value?: string | number | null | undefined }) {
  if (value === undefined || value === null || value === '') return null
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
      <p className="text-sm text-slate-900 tabular-nums">{String(value)}</p>
    </div>
  )
}

function InfoLink({ label, url }: { label: string; url?: string | null | undefined }) {
  if (!url) return null
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-primary-600 hover:text-primary-700 hover:underline transition-colors truncate block"
      >
        {url.replace(/^https?:\/\//, '')}
      </a>
    </div>
  )
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  // Only render if at least one child is non-null
  const childArray = Array.isArray(children) ? children : [children]
  const hasContent = childArray.some((child) => child !== null && child !== undefined && child !== false)
  if (!hasContent) return null
  return (
    <Card padding="md" className="border-slate-200">
      <CardHeader title={title} />
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm mt-3">
        {children}
      </div>
    </Card>
  )
}

function OverviewTab({ client, onSwitchTab }: { client: ClientWithRelations; onSwitchTab: (tab: TabId) => void }) {
  const navigate = useNavigate()
  const activeProjects = client.projects?.filter((p) => p.status === 'ACTIVE').length ?? 0
  const totalProjects = client._count?.projects ?? 0
  const totalTasks = client._count?.tasks ?? 0

  const fmtCurrency = (v?: number | null) =>
    v !== undefined && v !== null
      ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
      : null

  const fullAddress = [client.address, client.city, client.province ? `(${client.province})` : null, client.zipCode]
    .filter(Boolean)
    .join(', ')

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Progetti Attivi"
          value={activeProjects}
          icon={FolderKanban}
          tone="primary"
          onClick={() => onSwitchTab('projects')}
        />
        <StatCard
          label="Progetti Totali"
          value={totalProjects}
          icon={CheckSquare}
          tone="success"
          onClick={() => onSwitchTab('projects')}
        />
        <StatCard
          label="Task Totali"
          value={totalTasks}
          icon={Users}
          tone="neutral"
          onClick={() => navigate(`/tasks?clientId=${client.id}`)}
        />
      </div>

      {/* Informazioni Generali */}
      <SectionCard title="Informazioni Generali">
        <InfoField label="Email" value={client.email} />
        <InfoField label="Telefono" value={client.phone} />
        <InfoField label="Sito Web" value={client.website} />
        <InfoField label="Settore" value={client.industry} />
        <InfoField label="Dimensione Azienda" value={client.companySize ? `${client.companySize} dipendenti` : undefined} />
        {fullAddress && <InfoField label="Indirizzo" value={fullAddress} />}
        {client.healthScore !== undefined && client.healthScore !== null && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-1">Health Score</p>
            <p className={cn(
              'text-sm font-semibold tabular-nums',
              client.healthScore >= 70 ? 'text-success-600' :
              client.healthScore >= 40 ? 'text-warning-600' : 'text-danger-600'
            )}>
              {client.healthScore}/100
            </p>
          </div>
        )}
        {client.notes && (
          <div className="col-span-2">
            <p className="text-xs font-medium text-slate-500 mb-1">Note</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{client.notes}</p>
          </div>
        )}
      </SectionCard>

      {/* Dati Fatturazione */}
      <SectionCard title="Dati Fatturazione">
        <InfoField label="Ragione Sociale" value={client.legalName} />
        <InfoField label="Partita IVA" value={client.vatNumber} />
        <InfoField label="Codice Fiscale" value={client.fiscalCode} />
        <InfoField label="Codice SDI" value={client.sdiCode} />
        <InfoField label="PEC" value={client.pecEmail} />
        <InfoField label="IBAN" value={client.iban} />
        <InfoField label="Indirizzo Fatturazione" value={client.billingAddress} />
        <InfoField label="Paese" value={client.country} />
        <InfoField label="Budget Totale" value={fmtCurrency(client.totalBudget)} />
        <InfoField label="Speso" value={fmtCurrency(client.totalSpent)} />
        <InfoField label="Fatturato Annuo" value={fmtCurrency(client.annualRevenue)} />
      </SectionCard>

      {/* Referente Principale */}
      <SectionCard title="Referente Principale">
        <InfoField label="Nome" value={client.contactName} />
        <InfoField label="Ruolo" value={client.contactRole} />
        <InfoField label="Email" value={client.contactEmail} />
        <InfoField label="Telefono" value={client.contactPhone} />
      </SectionCard>

      {/* Profili Social */}
      <SectionCard title="Profili Social">
        <InfoLink label="LinkedIn" url={client.linkedinUrl} />
        <InfoLink label="Facebook" url={client.facebookUrl} />
        <InfoLink label="Instagram" url={client.instagramUrl} />
        <InfoLink label="TikTok" url={client.tiktokUrl} />
        <InfoLink label="YouTube" url={client.youtubeUrl} />
        <InfoLink label="X (Twitter)" url={client.xUrl} />
      </SectionCard>

      {/* Recent projects preview */}
      {client.projects && client.projects.length > 0 && (
        <Card padding="md" className="border-slate-200">
          <CardHeader title="Progetti Recenti" />
          <div className="space-y-2 mt-2">
            {client.projects.slice(0, 5).map((project) => (
              <button
                key={project.id}
                type="button"
                className="w-full text-left flex items-center gap-3 py-1.5 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <span className={cn(
                  'px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0',
                  PROJECT_STATUS_COLORS[project.status]
                )}>
                  {PROJECT_STATUS_LABELS[project.status]}
                </span>
                <span className="text-sm text-slate-900 truncate">{project.name}</span>
                {project.endDate && (
                  <span className="text-xs text-slate-400 ml-auto flex-shrink-0 tabular-nums">
                    {new Date(project.endDate).toLocaleDateString('it-IT')}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

// ============================================================
// PROJECTS TAB
// ============================================================

function ProjectsTab({ clientId }: { clientId: string }) {
  const navigate = useNavigate()
  const { data, isLoading, isError, error } = useQuery<Project[]>({
    queryKey: ['client-projects', clientId],
    queryFn: async () => {
      const res = await projectsApi.list({ clientId })
      const raw = res.data as any
      // Handle unwrapped or double-wrapped
      const payload = raw?.data ?? raw
      if (Array.isArray(payload)) return payload
      return payload?.items ?? []
    },
    enabled: Boolean(clientId),
  })

  if (isLoading) return <TabSkeleton rows={4} />
  if (isError) return <ErrorBanner message={getApiErrorMessage(error)} />

  const projects = data ?? []

  return (
    <div className="space-y-3">
      {projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-12 bg-white rounded-2xl border border-slate-200 shadow-card">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 mb-3">
            <FolderKanban className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-sm font-medium text-slate-900">Nessun progetto</p>
          <p className="text-xs text-slate-500 mt-0.5">Nessun progetto associato a questo cliente</p>
        </div>
      ) : (
        projects.map((project) => (
          <button
            key={project.id}
            type="button"
            className="w-full text-left flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200 shadow-card hover:border-slate-300 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
            onClick={() => navigate(`/projects/${project.id}`)}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900">{project.name}</p>
              {project.description && (
                <p className="text-xs text-slate-500 mt-0.5 truncate">{project.description}</p>
              )}
              {project.endDate && (
                <p className="text-xs text-slate-400 mt-0.5 tabular-nums">
                  Scadenza: {new Date(project.endDate).toLocaleDateString('it-IT')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {(project.moduleTypes ?? []).length > 0 && (
                <span className="hidden sm:inline text-xs text-slate-500">
                  {(project.moduleTypes ?? []).map((m: string) => MODULE_LABELS[m as keyof typeof MODULE_LABELS] ?? m).join(', ')}
                </span>
              )}
              <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', PROJECT_STATUS_COLORS[project.status])}>
                {PROJECT_STATUS_LABELS[project.status]}
              </span>
            </div>
          </button>
        ))
      )}
    </div>
  )
}

// ============================================================
// QUOTES TAB
// ============================================================

const QUOTE_STATUS_COLORS: Record<QuoteStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  SENT: 'bg-primary-50 text-primary-700',
  ACCEPTED: 'bg-success-50 text-success-700',
  REJECTED: 'bg-danger-50 text-danger-700',
  EXPIRED: 'bg-warning-50 text-warning-700',
  SUPERSEDED: 'bg-warning-50 text-warning-700',
}
const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  DRAFT: 'Bozza', SENT: 'Inviato', ACCEPTED: 'Accettato', REJECTED: 'Rifiutato', EXPIRED: 'Scaduto', SUPERSEDED: 'Sostituito',
}

function QuotesTab({ clientId }: { clientId: string }) {
  const navigate = useNavigate()
  const { data, isLoading, isError, error } = useQuery<Quote[]>({
    queryKey: ['client-quotes', clientId],
    queryFn: async () => {
      const res = await quotesApi.list({ clientId })
      const raw = res.data as any
      const payload = raw?.data ?? raw
      if (Array.isArray(payload)) return payload
      return payload?.items ?? []
    },
    enabled: Boolean(clientId),
  })

  if (isLoading) return <TabSkeleton rows={3} />
  if (isError) return <ErrorBanner message={getApiErrorMessage(error)} />

  const quotes = data ?? []

  const fmtCurrency = (v: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v)

  return (
    <div className="space-y-3">
      {quotes.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-12 bg-white rounded-2xl border border-slate-200 shadow-card">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 mb-3">
            <FileText className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-sm font-medium text-slate-900">Nessun preventivo</p>
          <p className="text-xs text-slate-500 mt-0.5">Nessun preventivo per questo cliente</p>
        </div>
      ) : (
        quotes.map((quote) => (
          <button
            key={quote.id}
            type="button"
            className="w-full text-left flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200 shadow-card hover:border-slate-300 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
            onClick={() => navigate(`/preventivi/${quote.id}`)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-slate-400 tabular-nums">{quote.quoteNumber}</span>
                <span className={cn(
                  'px-2 py-0.5 text-xs font-medium rounded-full',
                  QUOTE_STATUS_COLORS[quote.status]
                )}>
                  {QUOTE_STATUS_LABELS[quote.status]}
                </span>
              </div>
              <p className="text-sm font-medium text-slate-900 mt-0.5">{quote.title}</p>
              <p className="text-xs text-slate-400 mt-0.5 tabular-nums">
                {new Date(quote.issueDate).toLocaleDateString('it-IT')}
                {quote._count && ` \u00B7 ${quote._count.lineItems} voci`}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-semibold text-slate-900 tabular-nums">{fmtCurrency(quote.grandTotal)}</p>
            </div>
          </button>
        ))
      )}
    </div>
  )
}

// ============================================================
// CONTACTS TAB
// ============================================================

interface ContactFormProps {
  initial?: Partial<NewContactForm>
  onSave: (form: NewContactForm) => void
  onCancel: () => void
  isSaving: boolean
  submitLabel?: string
}

function ContactForm({ initial, onSave, onCancel, isSaving, submitLabel = 'Salva' }: ContactFormProps) {
  const [form, setForm] = useState<NewContactForm>({ ...EMPTY_CONTACT, ...initial })

  function patch(patch: Partial<NewContactForm>) {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  return (
    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Nome <span className="text-danger-500">*</span></label>
          <input
            type="text"
            className={inputClass}
            value={form.firstName}
            onChange={(e) => patch({ firstName: e.target.value })}
            placeholder="Mario"
          />
        </div>
        <div>
          <label className={labelClass}>Cognome <span className="text-danger-500">*</span></label>
          <input
            type="text"
            className={inputClass}
            value={form.lastName}
            onChange={(e) => patch({ lastName: e.target.value })}
            placeholder="Rossi"
          />
        </div>
        <div>
          <label className={labelClass}>Email</label>
          <input
            type="email"
            className={inputClass}
            value={form.email}
            onChange={(e) => patch({ email: e.target.value })}
            placeholder="mario.rossi@example.com"
          />
        </div>
        <div>
          <label className={labelClass}>Telefono</label>
          <input
            type="tel"
            className={inputClass}
            value={form.phone}
            onChange={(e) => patch({ phone: e.target.value })}
            placeholder="+39 02 1234567"
          />
        </div>
        <div>
          <label className={labelClass}>Ruolo / Posizione</label>
          <input
            type="text"
            className={inputClass}
            value={form.role}
            onChange={(e) => patch({ role: e.target.value })}
            placeholder="Es. Marketing Manager"
          />
        </div>
        <div className="flex items-center gap-2 pt-5">
          <input
            id="isPrimary"
            type="checkbox"
            checked={form.isPrimary}
            onChange={(e) => patch({ isPrimary: e.target.checked })}
            className="w-4 h-4 rounded border-slate-300 text-primary-600"
          />
          <label htmlFor="isPrimary" className="text-xs text-slate-700">Contatto primario</label>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onSave(form)}
          disabled={isSaving || !form.firstName.trim() || !form.lastName.trim()}
          className="px-3.5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {isSaving ? 'Salvataggio...' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3.5 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
        >
          Annulla
        </button>
      </div>
    </div>
  )
}

function ContactsTab({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingContactId, setEditingContactId] = useState<string | null>(null)

  const { data, isLoading, isError, error } = useQuery<Contact[]>({
    queryKey: ['client-contacts', clientId],
    queryFn: async () => {
      const res = await clientsApi.getContacts(clientId)
      const raw = res.data as any
      const payload = raw?.data ?? raw
      return Array.isArray(payload) ? payload : []
    },
    enabled: Boolean(clientId),
  })

  const addMutation = useMutation({
    mutationFn: (form: NewContactForm) =>
      clientsApi.addContact(clientId, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        role: form.role.trim() || undefined,
        isPrimary: form.isPrimary,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client-contacts', clientId] })
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] })
      setShowAddForm(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ contactId, form }: { contactId: string; form: NewContactForm }) =>
      clientsApi.updateContact(clientId, contactId, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        role: form.role.trim() || undefined,
        isPrimary: form.isPrimary,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client-contacts', clientId] })
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] })
      setEditingContactId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (contactId: string) => clientsApi.deleteContact(clientId, contactId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client-contacts', clientId] })
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] })
    },
  })

  if (isLoading) return <TabSkeleton rows={3} />
  if (isError) return <ErrorBanner message={getApiErrorMessage(error)} />

  const contacts = data ?? []

  return (
    <div className="space-y-3">
      {/* Add button */}
      {!showAddForm && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Contatto
          </button>
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <ContactForm
          onSave={(form) => addMutation.mutate(form)}
          onCancel={() => setShowAddForm(false)}
          isSaving={addMutation.isPending}
          submitLabel="Aggiungi"
        />
      )}

      {/* Add mutation error */}
      {addMutation.isError && (
        <ErrorBanner message={getApiErrorMessage(addMutation.error)} />
      )}

      {/* Empty state */}
      {contacts.length === 0 && !showAddForm && (
        <div className="flex flex-col items-center justify-center text-center py-12 bg-white rounded-2xl border border-slate-200 shadow-card">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 mb-3">
            <Users className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-sm font-medium text-slate-900">Nessun contatto</p>
          <p className="text-xs text-slate-500 mt-0.5">Nessun contatto registrato per questo cliente</p>
        </div>
      )}

      {/* Contact list */}
      {contacts.map((contact) => (
        <div key={contact.id}>
          {editingContactId === contact.id ? (
            <ContactForm
              initial={{
                firstName: contact.firstName,
                lastName: contact.lastName,
                email: contact.email ?? '',
                phone: contact.phone ?? '',
                role: contact.role ?? '',
                isPrimary: contact.isPrimary,
              }}
              onSave={(form) => updateMutation.mutate({ contactId: contact.id, form })}
              onCancel={() => setEditingContactId(null)}
              isSaving={updateMutation.isPending}
              submitLabel="Salva Modifiche"
            />
          ) : (
            <div className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200 shadow-card">
              {/* Avatar */}
              <div className="w-9 h-9 bg-primary-50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-primary-700">
                  {contact.firstName.charAt(0).toUpperCase()}
                </span>
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-slate-900">
                    {contact.firstName} {contact.lastName}
                  </p>
                  {contact.isPrimary && (
                    <Badge variant="info" size="sm">Primario</Badge>
                  )}
                </div>
                {contact.role && <p className="text-xs text-slate-500">{contact.role}</p>}
              </div>
              {/* Action icons */}
              <div className="flex items-center gap-3 text-slate-400">
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className="hover:text-primary-600 transition-colors" title={contact.email}>
                    <Mail className="w-4 h-4" />
                  </a>
                )}
                {contact.phone && (
                  <a href={`tel:${contact.phone}`} className="hover:text-primary-600 transition-colors" title={contact.phone}>
                    <Phone className="w-4 h-4" />
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setEditingContactId(contact.id)}
                  className="hover:text-slate-700 transition-colors"
                  title="Modifica contatto"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (await confirm({
                      title: 'Rimuovere contatto',
                      message: `Vuoi rimuovere il contatto ${contact.firstName} ${contact.lastName}?`,
                      danger: true,
                    })) {
                      deleteMutation.mutate(contact.id)
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="hover:text-danger-600 transition-colors disabled:opacity-50"
                  title="Elimina contatto"
                  aria-label={`Elimina contatto ${contact.firstName} ${contact.lastName}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {deleteMutation.isError && (
        <ErrorBanner message={getApiErrorMessage(deleteMutation.error)} />
      )}
      {updateMutation.isError && (
        <ErrorBanner message={getApiErrorMessage(updateMutation.error)} />
      )}
    </div>
  )
}

// ============================================================
// DOCUMENTS TAB
// ============================================================

interface UploadedFile {
  id: string
  filename: string
  url: string
  mimeType?: string
  size?: number
  createdAt: string
}

function DocumentsTab({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient()
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  // Fetch documents linked to this client.
  // NB: il backend espone solo GET /files/:id, non un endpoint di list per cliente.
  // L'errore viene propagato a isError (niente catch silenzioso) cosi lo stato di
  // errore/riprova diventa visibile finche il backend non aggiunge GET /files?clientId=...
  const { data, isLoading, isError, error, refetch } = useQuery<UploadedFile[]>({
    queryKey: ['client-documents', clientId],
    queryFn: async () => {
      const res = await filesApi.getFile(`?clientId=${clientId}`)
      const raw = res.data as any
      const payload = raw?.data ?? raw
      return Array.isArray(payload) ? payload : []
    },
    enabled: Boolean(clientId),
  })

  const deleteMutation = useMutation({
    mutationFn: (fileId: string) => filesApi.deleteFile(fileId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client-documents', clientId] })
    },
  })

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError(null)
    setIsUploading(true)

    const formData = new FormData()
    // multer su /files/upload usa upload.array('files') e legge entityId/entityType
    formData.append('files', file)
    formData.append('entityType', 'CLIENT')
    formData.append('entityId', clientId)

    try {
      await filesApi.upload(formData)
      toast.success('Documento caricato')
      void refetch()
    } catch (err) {
      setUploadError(getApiErrorMessage(err))
    } finally {
      setIsUploading(false)
    }
    // Reset input so the same file can be re-uploaded
    e.target.value = ''
  }

  if (isLoading) return <TabSkeleton rows={3} />
  if (isError) return <ErrorBanner message={getApiErrorMessage(error)} />

  const docs = data ?? []

  return (
    <div className="space-y-3">
      {/* Upload button */}
      <div className="flex justify-end">
        <label
          className={cn(
            'flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg transition-colors',
            isUploading ? 'opacity-60 cursor-not-allowed' : 'hover:bg-primary-700 cursor-pointer'
          )}
        >
          {isUploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Caricamento...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Carica Documento
            </>
          )}
          <input
            type="file"
            className="hidden"
            disabled={isUploading}
            onClick={() => setUploadError(null)}
            onChange={handleFileUpload}
          />
        </label>
      </div>

      {uploadError && <ErrorBanner message={uploadError} />}
      {deleteMutation.isError && <ErrorBanner message={getApiErrorMessage(deleteMutation.error)} />}

      {docs.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-12 bg-white rounded-2xl border border-slate-200 shadow-card">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 mb-3">
            <FileText className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-sm font-medium text-slate-900">Nessun documento</p>
          <p className="text-xs text-slate-500 mt-0.5">Nessun documento caricato per questo cliente</p>
        </div>
      ) : (
        docs.map((doc) => (
          <div key={doc.id} className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200 shadow-card">
            <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{doc.filename}</p>
              <p className="text-xs text-slate-400 mt-0.5 tabular-nums">
                {new Date(doc.createdAt).toLocaleDateString('it-IT')}
                {doc.size && ` · ${(doc.size / 1024).toFixed(1)} KB`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Apri
              </a>
              <button
                type="button"
                onClick={async () => {
                  if (await confirm({
                    title: 'Eliminare documento',
                    message: `Vuoi eliminare "${doc.filename}"?`,
                    danger: true,
                  })) {
                    deleteMutation.mutate(doc.id)
                  }
                }}
                disabled={deleteMutation.isPending}
                className="p-1.5 text-slate-400 hover:text-danger-600 transition-colors disabled:opacity-50"
                aria-label={`Elimina documento ${doc.filename}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  )
}

// ============================================================
// CREDENTIALS TAB (solo Super Admin)
// ============================================================

interface CredentialItem {
  id: string
  clientId: string
  label: string
  url?: string | null
  username?: string | null
  hasPassword: boolean
  notes?: string | null
  createdAt: string
  updatedAt: string
}

interface NewCredentialForm {
  label: string
  url: string
  username: string
  password: string
  notes: string
}

const EMPTY_CREDENTIAL: NewCredentialForm = {
  label: '', url: '', username: '', password: '', notes: '',
}

const SERVICE_PRESETS = [
  'Instagram', 'Facebook', 'TikTok', 'LinkedIn', 'YouTube', 'X (Twitter)',
  'Google Ads', 'Meta Business', 'WordPress', 'Hosting', 'Dominio',
  'Google Analytics', 'Google Search Console', 'Mailchimp', 'Altro',
]

function CredentialFormInline({ initial, onSave, onCancel, isSaving, submitLabel = 'Salva' }: {
  initial?: Partial<NewCredentialForm>
  onSave: (form: NewCredentialForm) => void
  onCancel: () => void
  isSaving: boolean
  submitLabel?: string
}) {
  const [form, setForm] = useState<NewCredentialForm>({ ...EMPTY_CREDENTIAL, ...initial })

  function patch(p: Partial<NewCredentialForm>) {
    setForm((prev) => ({ ...prev, ...p }))
  }

  return (
    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Etichetta <span className="text-danger-500">*</span></label>
          <div className="flex gap-2">
            <input
              type="text"
              className={inputClass}
              value={form.label}
              onChange={(e) => patch({ label: e.target.value })}
              placeholder="Es. Instagram @brand"
              list="service-presets"
            />
            <datalist id="service-presets">
              {SERVICE_PRESETS.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
        </div>
        <div>
          <label className={labelClass}>URL</label>
          <input
            type="url"
            className={inputClass}
            value={form.url}
            onChange={(e) => patch({ url: e.target.value })}
            placeholder="https://..."
          />
        </div>
        <div>
          <label className={labelClass}>Username</label>
          <input
            type="text"
            className={inputClass}
            value={form.username}
            onChange={(e) => patch({ username: e.target.value })}
            placeholder="nome.utente"
          />
        </div>
        <div>
          <label className={labelClass}>Password <span className="text-danger-500">*</span></label>
          <input
            type="text"
            className={inputClass}
            value={form.password}
            onChange={(e) => patch({ password: e.target.value })}
            placeholder={initial ? '(lascia vuoto per non cambiare)' : 'password'}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>Note</label>
          <input
            type="text"
            className={inputClass}
            value={form.notes}
            onChange={(e) => patch({ notes: e.target.value })}
            placeholder="Es. 2FA attivo, codice su telefono Mario"
          />
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onSave(form)}
          disabled={isSaving || !form.label.trim() || (!initial && !form.password.trim())}
          className="px-3.5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {isSaving ? 'Salvataggio...' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3.5 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
        >
          Annulla
        </button>
      </div>
    </div>
  )
}

function CredentialRow({ cred, clientId, onEdit }: {
  cred: CredentialItem
  clientId: string
  onEdit: () => void
}) {
  const queryClient = useQueryClient()
  const [showPassword, setShowPassword] = useState(false)
  const [password, setPassword] = useState<string | null>(null)
  const [loadingPw, setLoadingPw] = useState(false)
  const [copied, setCopied] = useState<'user' | 'pass' | null>(null)

  const deleteMutation = useMutation({
    mutationFn: () => clientsApi.deleteCredential(clientId, cred.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client-credentials', clientId] })
    },
  })

  async function togglePassword() {
    if (showPassword) {
      setShowPassword(false)
      return
    }
    if (password) {
      setShowPassword(true)
      return
    }
    setLoadingPw(true)
    try {
      const res = await clientsApi.getCredentialPassword(clientId, cred.id)
      const raw = res.data as any
      const pw = raw?.data?.password ?? raw?.password ?? ''
      setPassword(pw)
      setShowPassword(true)
    } catch {
      toast.error('Errore nel recupero della password')
    } finally {
      setLoadingPw(false)
    }
  }

  async function copyToClipboard(text: string, type: 'user' | 'pass') {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 1500)
    } catch { /* */ }
  }

  return (
    <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-card">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
          <KeyRound className="w-4 h-4 text-primary-600" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-2">
          {/* Label + actions on same row */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="text-sm font-medium text-slate-900 truncate">{cred.label}</span>
              {cred.url && (
                <a
                  href={cred.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-primary-600 transition-colors flex-shrink-0"
                  title={cred.url}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={onEdit}
                className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors"
                title="Modifica"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (await confirm({
                    title: 'Eliminare credenziale',
                    message: `Vuoi eliminare la credenziale "${cred.label}"?`,
                    danger: true,
                  })) {
                    deleteMutation.mutate()
                  }
                }}
                disabled={deleteMutation.isPending}
                className="p-1.5 text-slate-400 hover:text-danger-600 transition-colors disabled:opacity-50"
                title="Elimina"
                aria-label={`Elimina credenziale ${cred.label}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Username */}
          {cred.username && (
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs text-slate-400 flex-shrink-0">User:</span>
              <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-700 font-mono truncate max-w-[140px] sm:max-w-none">{cred.username}</code>
              <button
                type="button"
                onClick={() => copyToClipboard(cred.username!, 'user')}
                className="text-slate-300 hover:text-slate-600 transition-colors flex-shrink-0"
                title="Copia username"
              >
                <Copy className="w-3 h-3" />
              </button>
              {copied === 'user' && <span className="text-[10px] text-success-600 flex-shrink-0">Copiato!</span>}
            </div>
          )}

          {/* Password */}
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-slate-400 flex-shrink-0">Pass:</span>
            {showPassword && password ? (
              <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-700 font-mono truncate max-w-[140px] sm:max-w-none">{password}</code>
            ) : (
              <span className="text-xs text-slate-300 flex-shrink-0">{'••••••••'}</span>
            )}
            <button
              type="button"
              onClick={togglePassword}
              disabled={loadingPw}
              className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50 flex-shrink-0"
              title={showPassword ? 'Nascondi' : 'Mostra password'}
            >
              {loadingPw ? (
                <span className="w-3.5 h-3.5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin inline-block" />
              ) : showPassword ? (
                <EyeOff className="w-3.5 h-3.5" />
              ) : (
                <Eye className="w-3.5 h-3.5" />
              )}
            </button>
            {showPassword && password && (
              <button
                type="button"
                onClick={() => copyToClipboard(password, 'pass')}
                className="text-slate-300 hover:text-slate-600 transition-colors flex-shrink-0"
                title="Copia password"
              >
                <Copy className="w-3 h-3" />
              </button>
            )}
            {copied === 'pass' && <span className="text-[10px] text-success-600 flex-shrink-0">Copiato!</span>}
          </div>

          {/* Notes */}
          {cred.notes && (
            <p className="text-xs text-slate-400 italic break-words">{cred.notes}</p>
          )}
        </div>
      </div>
    </div>
  )
}

function CredentialsTab({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const { data, isLoading, isError, error } = useQuery<CredentialItem[]>({
    queryKey: ['client-credentials', clientId],
    queryFn: async () => {
      const res = await clientsApi.getCredentials(clientId)
      const raw = res.data as any
      const payload = raw?.data ?? raw
      return Array.isArray(payload) ? payload : []
    },
    enabled: Boolean(clientId),
  })

  const addMutation = useMutation({
    mutationFn: (form: NewCredentialForm) =>
      clientsApi.createCredential(clientId, {
        label: form.label.trim(),
        url: form.url.trim() || undefined,
        username: form.username.trim() || undefined,
        password: form.password,
        notes: form.notes.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client-credentials', clientId] })
      setShowAddForm(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ credentialId, form }: { credentialId: string; form: NewCredentialForm }) =>
      clientsApi.updateCredential(clientId, credentialId, {
        label: form.label.trim(),
        url: form.url.trim() || undefined,
        username: form.username.trim() || undefined,
        password: form.password.trim() || undefined,
        notes: form.notes.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client-credentials', clientId] })
      setEditingId(null)
    },
  })

  if (isLoading) return <TabSkeleton rows={3} />
  if (isError) return <ErrorBanner message={getApiErrorMessage(error)} />

  const credentials = data ?? []

  return (
    <div className="space-y-3">
      {/* Add button */}
      {!showAddForm && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Credenziale
          </button>
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <CredentialFormInline
          onSave={(form) => addMutation.mutate(form)}
          onCancel={() => setShowAddForm(false)}
          isSaving={addMutation.isPending}
          submitLabel="Aggiungi"
        />
      )}

      {addMutation.isError && <ErrorBanner message={getApiErrorMessage(addMutation.error)} />}

      {/* Empty state */}
      {credentials.length === 0 && !showAddForm && (
        <div className="flex flex-col items-center justify-center text-center py-12 bg-white rounded-2xl border border-slate-200 shadow-card">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 mb-3">
            <KeyRound className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-sm font-medium text-slate-900">Nessuna credenziale salvata</p>
          <p className="text-xs text-slate-500 mt-0.5">Aggiungi le password di social, siti e servizi del cliente</p>
        </div>
      )}

      {/* Credential list */}
      {credentials.map((cred) => (
        <div key={cred.id}>
          {editingId === cred.id ? (
            <CredentialFormInline
              initial={{
                label: cred.label,
                url: cred.url ?? '',
                username: cred.username ?? '',
                password: '',
                notes: cred.notes ?? '',
              }}
              onSave={(form) => updateMutation.mutate({ credentialId: cred.id, form })}
              onCancel={() => setEditingId(null)}
              isSaving={updateMutation.isPending}
              submitLabel="Salva Modifiche"
            />
          ) : (
            <CredentialRow
              cred={cred}
              clientId={clientId}
              onEdit={() => setEditingId(cred.id)}
            />
          )}
        </div>
      ))}

      {updateMutation.isError && <ErrorBanner message={getApiErrorMessage(updateMutation.error)} />}
    </div>
  )
}

// ============================================================
// MAIN PAGE
// ============================================================

export function ClientDashboardPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const isSuperAdmin = user?.isSuperAdmin === true
  // Tab iniziale pilotabile da URL (?tab=credentials) per i deep-link, es. il
  // bottone "Vai →" dell'assistente AI dopo aver salvato una credenziale.
  const [searchParams] = useSearchParams()
  const VALID_TABS: TabId[] = ['overview', 'projects', 'quotes', 'contacts', 'documents', 'credentials']
  const requestedTab = searchParams.get('tab') as TabId | null
  const initialTab: TabId = requestedTab && VALID_TABS.includes(requestedTab) ? requestedTab : 'overview'
  const [activeTab, setActiveTab] = useState<TabId>(initialTab)
  const [showEditForm, setShowEditForm] = useState(false)

  const TABS = [
    ...BASE_TABS,
    ...(isSuperAdmin ? [{ id: 'credentials' as TabId, label: 'Credenziali' }] : []),
  ]

  const {
    data: clientResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<ClientWithRelations>({
    queryKey: ['client', id],
    queryFn: async () => {
      const res = await clientsApi.getById(id!)
      // Interceptor already unwraps { success, data } envelope
      const raw = res.data as any
      // Handle both unwrapped and double-wrapped cases
      if (raw && typeof raw === 'object' && 'success' in raw && 'data' in raw) {
        return raw.data as ClientWithRelations
      }
      return raw as ClientWithRelations
    },
    enabled: Boolean(id),
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
  })

  // Full-page loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-5 bg-slate-100 rounded w-32" />
        <div className="h-24 bg-slate-100 rounded-2xl" />
        <div className="h-8 bg-slate-100 rounded w-full" />
        <div className="space-y-3">
          <div className="h-16 bg-slate-100 rounded-2xl" />
          <div className="h-16 bg-slate-100 rounded-2xl" />
        </div>
      </div>
    )
  }

  // Full-page error
  if (isError || !clientResponse) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/clients')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna ai Clienti
        </button>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-6 text-center space-y-4">
          <div className="w-10 h-10 bg-danger-50 rounded-lg flex items-center justify-center mx-auto">
            <AlertCircle className="w-5 h-5 text-danger-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">
              {isError ? 'Errore nel caricamento' : 'Cliente non trovato'}
            </h3>
            <p className="text-sm text-slate-500">
              {isError ? getApiErrorMessage(error) : 'Il cliente richiesto non esiste o è stato eliminato.'}
            </p>
          </div>
          {isError && (
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              Riprova
            </button>
          )}
        </div>
      </div>
    )
  }

  const client = clientResponse

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      <button
        onClick={() => navigate('/clients')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Torna ai Clienti
      </button>

      {/* Client header card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-5">
        <div className="flex items-start gap-4">
          {/* Logo / avatar */}
          {client.logo ? (
            <img
              src={client.logo}
              alt={client.name}
              className="w-14 h-14 rounded-xl object-contain bg-slate-50 border border-slate-200 flex-shrink-0"
            />
          ) : (
            <div className="w-14 h-14 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-semibold text-primary-600">{client.name.charAt(0)}</span>
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{client.name}</h1>
              <Badge variant={STATUS_VARIANT[client.status]} dot>{STATUS_LABEL[client.status]}</Badge>
            </div>
            <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500 flex-wrap">
              {client.address && <span>{client.address}</span>}
              {client.website && (
                <a
                  href={client.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-primary-600 transition-colors"
                >
                  <Globe className="w-3.5 h-3.5" />
                  {client.website.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>
          </div>

          <button
            className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex-shrink-0"
            onClick={() => setShowEditForm(true)}
          >
            <Pencil className="w-4 h-4" />
            Modifica
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap',
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && <OverviewTab client={client} onSwitchTab={setActiveTab} />}
        {activeTab === 'projects' && <ProjectsTab clientId={id!} />}
        {activeTab === 'quotes' && <QuotesTab clientId={id!} />}
        {activeTab === 'contacts' && <ContactsTab clientId={id!} />}
        {activeTab === 'documents' && <DocumentsTab clientId={id!} />}
        {activeTab === 'credentials' && isSuperAdmin && <CredentialsTab clientId={id!} />}
      </div>

      {/* Edit client modal */}
      {showEditForm && (
        <ClientForm
          client={client}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => {
            void queryClient.invalidateQueries({ queryKey: ['client', id] })
          }}
        />
      )}
    </div>
  )
}
